/**
 * @license
 * Copyright 2023 Google LLC
 * SPDX-License-Identifier: Apache-2.0
 */
import { callHandler, handlersFromKits } from "../handler.js";
import { streamsToAsyncIterable } from "../stream.js";
import { asRuntimeKit } from "../kits/ctors.js";
import { KitBuilder } from "../kits/builder.js";
import { createTunnelKit, readConfig } from "./tunnel.js";
import { timestamp } from "../timestamp.js";
import { inflateData } from "../data/inflate-deflate.js";
const getHandlerConfig = (type, config = []) => {
    const handlerConfig = config.find((arg) => {
        if (typeof arg === "string")
            return arg === type;
        else
            return arg.node === type;
    });
    if (typeof handlerConfig === "string") {
        return {
            node: handlerConfig,
        };
    }
    return handlerConfig;
};
const makeSerializable = (data) => {
    if (data["$error"]) {
        const error = data["$error"];
        error.error =
            error.error instanceof Error ? error.error.message : error.error;
    }
    return data;
};
export class ProxyServer {
    #transport;
    constructor(transport) {
        this.#transport = transport;
    }
    async serve(config) {
        const { kits, store } = config;
        const stream = this.#transport.createServerStream();
        const tunnelKit = createTunnelKit(readConfig(config), handlersFromKits(kits));
        const handlers = tunnelKit.handlers;
        for await (const request of streamsToAsyncIterable(stream.writableResponses, stream.readableRequests)) {
            const [type] = request.data;
            if (type === "end") {
                break;
            }
            if (type !== "proxy") {
                request.reply([
                    "error",
                    { error: "Expected proxy request.", timestamp: timestamp() },
                ]);
                continue;
            }
            const [, { node, inputs }] = request.data;
            const handlerConfig = getHandlerConfig(node.type, config.proxy);
            const handler = handlerConfig ? handlers[node.type] : undefined;
            if (!handler) {
                request.reply([
                    "error",
                    {
                        error: "Can't proxy a node of this node type.",
                        timestamp: timestamp(),
                    },
                ]);
                continue;
            }
            try {
                const result = await callHandler(handler, inputs, {
                    descriptor: node,
                    store,
                });
                if (!result) {
                    request.reply([
                        "error",
                        { error: "Handler returned nothing.", timestamp: timestamp() },
                    ]);
                    continue;
                }
                const outputs = store
                    ? (await inflateData(store, makeSerializable(result)))
                    : result;
                request.reply(["proxy", { outputs }]);
            }
            catch (e) {
                request.reply([
                    "error",
                    { error: e.message, timestamp: timestamp() },
                ]);
            }
        }
    }
}
export class ProxyClient {
    #transport;
    constructor(transport) {
        this.#transport = transport;
    }
    shutdownServer() {
        const stream = this.#transport.createClientStream();
        const writer = stream.writableRequests.getWriter();
        writer.write(["end", { timestamp: timestamp() }]);
        writer.close();
    }
    async proxy(node, inputs, context) {
        const stream = this.#transport.createClientStream();
        const writer = stream.writableRequests.getWriter();
        const reader = stream.readableResponses.getReader();
        const inflateToFileData = isGeminiApiFetch(node, inputs);
        const store = context.store;
        inputs = store
            ? (await inflateData(store, inputs, context.base, inflateToFileData))
            : inputs;
        writer.write(["proxy", { node, inputs }]);
        writer.close();
        const result = await reader.read();
        if (result.done)
            throw new Error("Unexpected proxy failure: empty response.");
        const [type] = result.value;
        if (type === "proxy") {
            const [, { outputs }] = result.value;
            return outputs;
        }
        else if (type === "error") {
            const [, { error }] = result.value;
            throw new Error(JSON.stringify(error));
        }
        else {
            throw new Error(`Unexpected proxy failure: unknown response type "${type}".`);
        }
    }
    createProxyKit(args = []) {
        const nodesToProxy = args.map((arg) => {
            if (typeof arg === "string")
                return arg;
            else
                return arg.node;
        });
        const proxiedNodes = Object.fromEntries(nodesToProxy.map((type) => {
            return [
                type,
                {
                    invoke: async (inputs, context) => {
                        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
                        const descriptor = context.descriptor;
                        const result = await this.proxy(descriptor, inputs, context);
                        return result;
                    },
                },
            ];
        }));
        return asRuntimeKit(new KitBuilder({ url: "proxy" }).build(proxiedNodes));
    }
}
function isGeminiApiFetch(node, inputs) {
    if (node.type !== "fetch")
        return false;
    return ("url" in inputs &&
        !!inputs.url &&
        typeof inputs.url === "string" &&
        inputs.url.startsWith("https://generativelanguage.googleapis.com"));
}
//# sourceMappingURL=proxy.js.map